import { useMemo, useState } from 'react';

import { Box, CircularProgress, Dialog, IconButton } from '@mui/material';
import { XIcon } from 'lucide-react';

import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { createDocumentUrl } from '@modules/_shared/utils/document-url';
import { type Language, useLocale } from '@shared/hooks/locale';
import { cssTokens } from '@shared/theme/StyleProvider';

type TemplateViewerDialogProps = {
  templateId: string;
  documentId: string;
  open: boolean;
  onClose: () => void;
  language?: Language;
  /** Override the broker context with this user's data (for MLS brochures) */
  overrideUserId?: string;
};

const TemplateViewerDialogContent = ({
  templateId,
  documentId,
  onClose,
  language: overrideLanguage,
  overrideUserId,
}: Omit<TemplateViewerDialogProps, 'open'>) => {
  const [loaded, setLoaded] = useState(false);
  const { language } = useLocale();

  const documentUrl = useMemo(() => {
    const url = createDocumentUrl(
      templateId,
      documentId,
      overrideLanguage ?? language,
    );
    url.searchParams.set('in-crm-dialog', 'true');
    url.searchParams.set(
      'timezone',
      Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
    );
    if (overrideUserId != null && overrideUserId !== '') {
      url.searchParams.set('overrideUserId', overrideUserId);
    }
    return url;
  }, [documentId, language, overrideLanguage, overrideUserId, templateId]);

  return (
    <>
      {loaded === false && (
        <>
          <Box
            css={{
              position: 'absolute',
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
              backgroundColor: cssTokens.grey300,
            }}
          />
          <Box
            css={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 64,
              backgroundColor: '#1a65af',
            }}
          />
          <CircularProgress
            disableShrink
            css={{
              position: 'absolute',
              left: 'calc(50% - 20px)',
              top: 'calc(50% - 20px)',
            }}
          />
        </>
      )}
      <Box
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: 'flex',
          width: 64,
          height: 64,
          justifyContent: 'center',
          alignItems: 'center',
          color: '#fff',
        }}
      >
        <IconButton color="inherit" onClick={onClose} aria-label="close">
          <XIcon size={20} />
        </IconButton>
      </Box>
      <iframe
        title="Document preview"
        src={documentUrl.href}
        style={{
          border: 0,
          width: '100%',
          height: '100%',
          visibility: loaded ? 'visible' : 'hidden',
        }}
        allow="clipboard-write"
        onLoad={() => {
          setLoaded(true);
        }}
      />
    </>
  );
};

export const TemplateViewerDialog = ({
  templateId,
  documentId,
  open,
  onClose,
  language,
  overrideUserId,
}: TemplateViewerDialogProps) => {
  return (
    <Dialog
      open={open}
      fullScreen
      onClose={onClose}
      slots={{
        transition: SlideUpTransition,
      }}
    >
      <TemplateViewerDialogContent
        templateId={templateId}
        documentId={documentId}
        onClose={onClose}
        language={language}
        overrideUserId={overrideUserId}
      />
    </Dialog>
  );
};
